/**
 * Error page - generic error and 404 states
 */
import { BaseElement } from '../base-element';
import { navigate } from '../../router';

export class ErrorPage extends BaseElement {
  connectedCallback(): void {
    this.updateRender();
  }

  private updateRender(): void {
    const code = this.getAttribute('code');
    const content = code === '404' ? this.renderNotFound() : this.renderGeneric();
    this.render(content);
    this.attachEventListeners();
  }

  private renderNotFound(): string {
    return `
      <div class="flex min-h-[420px] items-center justify-center px-4 py-12" data-testid="error-404">
        <div class="relative w-full max-w-3xl overflow-hidden rounded-[32px] border border-neutral-200 bg-white shadow-[0_24px_70px_rgba(48,52,37,0.12)] dark:border-white/10 dark:bg-surface-dark">
          <div class="pointer-events-none absolute -left-14 top-0 h-40 w-40 rounded-full bg-primary-100 blur-3xl dark:bg-primary-900/25"></div>
          <div class="pointer-events-none absolute -right-10 bottom-0 h-44 w-44 rounded-full bg-secondary-100 blur-3xl dark:bg-secondary-900/20"></div>
          <div class="relative flex flex-col items-center px-6 py-12 text-center sm:px-10">
            <div class="inline-flex items-center rounded-full bg-primary-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700 dark:bg-primary-500/15 dark:text-primary-200">Venom Dog Sports</div>
            <div class="mb-6 mt-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary-50 text-primary-500 ring-8 ring-white dark:bg-neutral-800 dark:ring-surface-dark">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-10 w-10">
                <path fill-rule="evenodd" d="M4.5 6.75a1.5 1.5 0 000 3h1.5v4.5H4.5a1.5 1.5 0 000 3H9a1.5 1.5 0 000-3H7.5v-4.5H9a1.5 1.5 0 000-3H4.5zm10.5 0a1.5 1.5 0 000 3h1.5v4.5H15a1.5 1.5 0 000 3h4.5a1.5 1.5 0 000-3H18v-4.5h1.5a1.5 1.5 0 000-3H15z" clip-rule="evenodd"/>
              </svg>
            </div>
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-secondary-600 dark:text-secondary-300">Off the course</p>
            <h1 class="mb-3 mt-3 text-3xl font-bold text-neutral-900 dark:text-white">Page Not Found</h1>
            <p class="mb-8 max-w-xl text-sm leading-7 text-neutral-600 dark:text-neutral-300">We couldn't find the page you're looking for. Head back to the trial list and pick up from the main Venom Dog Sports flow.</p>
            <button class="inline-flex items-center justify-center rounded-xl bg-primary-500 px-5 py-2 text-sm font-semibold text-white shadow-xs transition-colors hover:bg-primary-600" data-testid="go-home-btn">Go to Home</button>
          </div>
        </div>
      </div>
    `;
  }

  private renderGeneric(): string {
    const params = new URLSearchParams(window.location.search);
    const supportId = params.get('supportId');
    const message = params.get('message') || 'An unexpected error occurred.';

    return `
      <div class="flex min-h-[420px] items-center justify-center px-4 py-12" data-testid="error-generic">
        <div class="relative w-full max-w-3xl overflow-hidden rounded-[32px] border border-neutral-200 bg-white shadow-[0_24px_70px_rgba(48,52,37,0.12)] dark:border-white/10 dark:bg-surface-dark">
          <div class="pointer-events-none absolute -left-14 top-0 h-40 w-40 rounded-full bg-error-50 blur-3xl dark:bg-error-500/10"></div>
          <div class="pointer-events-none absolute -right-10 bottom-0 h-44 w-44 rounded-full bg-secondary-100 blur-3xl dark:bg-secondary-900/20"></div>
          <div class="relative flex flex-col items-center px-6 py-12 text-center sm:px-10">
            <div class="inline-flex items-center rounded-full bg-primary-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700 dark:bg-primary-500/15 dark:text-primary-200">Venom Dog Sports</div>
            <div class="mb-6 mt-6 flex h-20 w-20 items-center justify-center rounded-full bg-error-50 text-error-500 ring-8 ring-white dark:bg-error-500/10 dark:ring-surface-dark">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-10 w-10">
                <path fill-rule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd"/>
              </svg>
            </div>
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-error-700 dark:text-error-300">Support is ready</p>
            <h1 class="mb-3 mt-3 text-3xl font-bold text-neutral-900 dark:text-white">Something Went Wrong</h1>
            <p class="mb-4 max-w-xl text-sm leading-7 text-neutral-600 dark:text-neutral-300" data-testid="error-message">${this.esc(message)}</p>
            ${supportId ? `<p class="mb-6 rounded-full bg-neutral-100 px-4 py-2 text-xs font-medium text-neutral-600 dark:bg-neutral-900 dark:text-neutral-300" data-testid="support-id">Support ID: ${this.esc(supportId!)}</p>` : '<div class="mb-6"></div>'}
            <p class="mb-8 max-w-xl text-sm leading-7 text-neutral-500 dark:text-neutral-400">If you contact support, include the ID above so the Venom Dog Sports team can trace the exact failure faster.</p>
            <div class="flex flex-wrap items-center justify-center gap-3">
              <button class="inline-flex items-center justify-center rounded-xl border border-neutral-200 px-5 py-2 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800" data-testid="try-again-btn">Try Again</button>
              <button class="inline-flex items-center justify-center rounded-xl bg-primary-500 px-5 py-2 text-sm font-semibold text-white shadow-xs transition-colors hover:bg-primary-600" data-testid="go-home-btn">Go to Home</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private attachEventListeners(): void {
    const goHomeBtn = this.$('[data-testid="go-home-btn"]');
    if (goHomeBtn) {
      goHomeBtn.addEventListener('click', () => navigate('/trials'));
    }

    const tryAgainBtn = this.$('[data-testid="try-again-btn"]');
    if (tryAgainBtn) {
      tryAgainBtn.addEventListener('click', () => window.history.back());
    }
  }
}

customElements.define('dt-error-page', ErrorPage);
